import { Check } from 'lucide-react';

import CtaLink from '@/components/CtaLink';
import { useLanguage } from '@/components/LanguageContext';
import ContactoForm from '@/components/landing/v3/ContactoForm';
import FaqAcordeon from '@/components/landing/v3/FaqAcordeon';
import CapturaProducto from '@/components/landing/v3/CapturaProducto';
import LandingShellV3 from '@/components/landing/v3/LandingShellV3';
import LogosMarquee from '@/components/landing/v3/LogosMarquee';
import SectionKicker from '@/components/landing/v3/SectionKicker';
import { pickLandingV3Content } from '@/pages/landingV3.content';

/**
 * MultiubicacionesV3 — la página para grupos y franquicias.
 *
 * Portada desde `Multiubicaciones.dc.html`. Es la página comercial del
 * producto: la que alguien con diez locales lee entera antes de dejar sus
 * datos, así que es también la que más afirmaciones concretas hace. Cuáles no
 * se han copiado, y por qué, está en la cabecera de
 * `landingV3.multiubicaciones.js`.
 *
 * Las seis secciones de función son la misma pieza repetida —kicker, titular,
 * dos párrafos, tres bullets y la caja de «Lo que te ahorra»—, así que van
 * recorriendo una lista y no escritas seis veces.
 *
 * El bloque de captación (`#hablemos`) ya estaba montado desde el PR 3: es lo
 * único que había aquí mientras el cuerpo llegaba.
 */
export default function MultiubicacionesV3() {
  const { language } = useLanguage();
  const raiz = pickLandingV3Content(language);
  const c = raiz.multiubicaciones;

  return (
    <LandingShellV3>
      <Hero c={c.hero} />
      <Problema c={c.problema} />
      <Horas c={c.horas} />

      {c.secciones.map((s) => (
        <Funcion key={s.clave} c={s} ahorraEtiqueta={c.ahorraEtiqueta} />
      ))}

      <Informe c={c.informe} />
      <section className="px-6 pt-[76px]">
        <div className="max-w-[1180px] mx-auto">
          <LogosMarquee titulo={c.logos} />
        </div>
      </section>
      <FaqAcordeon titulo={c.faqTitulo} preguntas={c.faq} />
      <Hablemos c={raiz.contacto} />
    </LandingShellV3>
  );
}

function Hero({ c }) {
  return (
    <section className="bg-white px-6 pb-6">
      <div className="max-w-[1180px] mx-auto text-center pt-4">
        <div className="inline-flex items-center bg-v3-surface border border-v3-line rounded-full px-[15px] py-2 text-[13px] font-medium text-v3-body">
          {c.badge}
        </div>

        <h1 className="text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.06] tracking-[-.035em] font-bold text-v3-ink mt-5 mx-auto max-w-[22ch]">
          {c.titulo}
          <span className="text-v3-golddeep">{c.acento}</span>
        </h1>

        <p className="text-[17px] lg:text-[18px] leading-[1.6] text-v3-body mt-[18px] mx-auto max-w-[58ch]">
          {c.sub}
        </p>

        <div className="mt-7">
          {/* Ancla, no registro: esta página no manda a `/register`, manda al
              formulario de más abajo. De ahí la meta `nav`. */}
          <CtaLink
            as="a"
            cta="multiubicaciones.hero.info"
            href="#hablemos"
            className="inline-block bg-v3-gold hover:bg-v3-golddeep text-v3-ink text-[17px] font-bold rounded-full px-[34px] py-[17px] transition-colors"
          >
            {c.cta}
          </CtaLink>
        </div>

        {/* `prioritaria`: entra en la primera pantalla, diferirla es lo que
            hace que la pagina parezca lenta. */}
        <CapturaProducto
          {...c.captura}
          prioritaria
          pie={c.pieCaptura}
          className="mt-10 max-w-[1000px] mx-auto"
        />
      </div>
    </section>
  );
}

function Problema({ c }) {
  return (
    <section className="px-6 pt-[72px]">
      <div className="max-w-[1180px] mx-auto bg-v3-surface rounded-3xl p-7 sm:p-10">
        <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[28ch]">
          {c.titulo}
        </h2>
        {[c.p1, c.p2, c.p3].map((p) => (
          <p key={p} className="text-base leading-[1.7] text-v3-body mt-4 max-w-[70ch]">
            {p}
          </p>
        ))}
      </div>
    </section>
  );
}

function Horas({ c }) {
  return (
    <section className="px-6 pt-9">
      <div className="max-w-[1180px] mx-auto bg-v3-greensoft rounded-3xl px-7 py-9 sm:px-10 flex flex-wrap gap-8 items-center">
        <p className="text-[40px] sm:text-[52px] font-bold tracking-[-.04em] leading-none text-v3-green flex-none">
          {c.cifra}
        </p>
        <p className="flex-1 min-w-[240px] text-[17px] leading-[1.55] text-v3-ink font-medium">
          {c.texto}
        </p>
      </div>
    </section>
  );
}

/**
 * Una sección de función.
 *
 * LA CAPTURA VA SIEMPRE A LA IZQUIERDA, como en el prototipo. Hubo una versión
 * que alternaba el lado para romper la monotonía de seis bloques iguales
 * seguidos; se retiró a petición de producto. Si alguien vuelve a tener la
 * idea: es deliberado que no alterne.
 */
function Funcion({ c, ahorraEtiqueta }) {
  return (
    <section className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto">
        <SectionKicker insignia={c.insignia}>{c.kicker}</SectionKicker>

        <div className="mt-[26px] max-w-[860px]">
          <h2 className="text-[27px] md:text-[32px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[26ch]">
            {c.titulo}
          </h2>
          <p className="text-[16.5px] leading-[1.7] text-v3-body mt-4 max-w-[70ch]">{c.p1}</p>
          <p className="text-[16.5px] leading-[1.7] text-v3-body mt-3.5 max-w-[70ch]">{c.p2}</p>
        </div>

        <div className="grid gap-8 lg:grid-cols-2 lg:gap-10 items-start mt-7">
          <CapturaProducto {...c.captura} />

          <div>
            <ul className="flex flex-col gap-3.5">
              {c.bullets.map((b) => (
                <li key={b.clave} className="flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    className="w-5 h-5 rounded-full bg-v3-goldsoft text-v3-golddeep flex items-center justify-center flex-none mt-[3px]"
                  >
                    <Check className="w-3 h-3" strokeWidth={3.5} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[15px] font-bold tracking-[-.01em] text-v3-ink">
                      {b.t}
                    </span>
                    <span className="block text-[14.5px] leading-[1.6] text-v3-body mt-0.5">
                      {b.d}
                    </span>
                  </span>
                </li>
              ))}
            </ul>

            <div className="bg-v3-surface rounded-2xl px-5 py-[18px] mt-[22px]">
              <div className="text-[11.5px] font-bold text-v3-faint">{ahorraEtiqueta}</div>
              <p className="text-[15px] leading-[1.6] text-v3-ink font-medium mt-1.5">
                {c.ahorra}
              </p>
            </div>

            {/* La letra pequeña de una función que promete menos de lo que
                parece. Va aquí y no escondida: enterarse de esto pagando es
                peor que leerlo ahora. */}
            {c.nota ? (
              <p className="text-[13px] leading-[1.6] text-v3-muted mt-3.5">{c.nota}</p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * El informe mensual.
 *
 * El diseño traía aquí una maqueta de 140 px: una tarjetita con el logo, un
 * mes y cuatro barras grises simulando la portada del PDF. Se sustituye por una
 * captura del informe de verdad, que es lo que se está vendiendo —y una página
 * real con sus benchmarks y su análisis de sentimiento convence mucho más que
 * cuatro barras de mentira—.
 *
 * Eso obliga a cambiar la proporción de la sección: la captura es apaisada
 * (1412×890) y en los 140 px del mockup no se leía nada. Pasa a dos columnas,
 * con la imagen a la izquierda, igual que las seis secciones de función.
 */
function Informe({ c }) {
  return (
    <section className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto bg-v3-surface rounded-3xl p-7 sm:p-10">
        <div className="grid gap-8 lg:grid-cols-2 lg:gap-10 items-center">
          <CapturaProducto {...c.captura} />

          <div>
            <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[26ch]">
              {c.titulo}
            </h2>
            <p className="text-base leading-[1.7] text-v3-body mt-4 max-w-[64ch]">{c.p1}</p>
            <p className="text-base leading-[1.7] text-v3-body mt-3.5 max-w-[64ch]">{c.p2}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Hablemos({ c }) {
  return (
    <section id="hablemos" className="max-w-[1180px] mx-auto px-6 pt-[76px] pb-20">
      {/*
        EL BLOQUE YA NO ES NEGRO. El diseño lo traía en `#111111` con la tarjeta
        blanca encima; sobre una página clara eso pesa como un anuncio y se lee
        antes el bloque que lo que pone dentro. Va sobre la misma superficie
        `#fbfbfa` que el resto de la V3.
      */}
      <div className="bg-v3-surface border border-v3-line rounded-3xl px-6 py-10 md:px-12 md:py-14">
        <div className="grid gap-10 lg:grid-cols-2 items-start">
          <div>
            <h2 className="text-[28px] md:text-[36px] leading-[1.12] tracking-[-.03em] font-bold text-v3-ink max-w-[20ch]">
              {c.titulo}
              <span className="text-v3-golddeep">{c.tituloAcento}</span>
            </h2>
            <p className="text-[16.5px] leading-[1.6] text-v3-body mt-4 max-w-[42ch]">
              {c.cuerpo}
            </p>
          </div>

          <ContactoForm ctaId="multiubicaciones.form.enviar" />
        </div>
      </div>
    </section>
  );
}
